import { RiArrowLeftLine, RiSearchLine } from '@remixicon/react';
import {
  Button,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  TextInput,
  Select,
  SelectItem,
} from '@tremor/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Pagination } from '../../../../ui/component/pagination';
import { CandidatesTable } from './CandidatesTable';
import { CandidateType } from '../../../../mirage/types';
import Fuse from 'fuse.js';
import { SavedCandidatesTable } from './SavedCandidates';
import ResumeDetailCard from './ResumeDetailCard';
import axios from 'axios'; // Import axios

// Define the base URL for the API
// TODO: Use environment variables for flexibility (e.g., process.env.REACT_APP_API_URL or import.meta.env.VITE_API_URL)
const API_BASE_URL = process.env.REACT_APP_API_HOST;

// Placeholder type for the actual API response structure
// Adjust this based on what `getAllResumes` actually returns
interface ResumeApiResponse {
  status: string;
  data: CandidateType[]; // Assuming the data array matches CandidateType structure
}

const RESUMES_PAGE_SIZE = 20;

export const ResumeManagment = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCurrentUser, setShowCurrentUser] = useState(false);
  const [candidateData, setCandidateData] = useState<CandidateType | {}>({});
  const [resumes, setResumes] = useState<CandidateType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Zero-based, as the Pagination component expects.
  const [page, setPage] = useState(0);

  // Fetch resumes from the backend API using axios
  useEffect(() => {
    const fetchResumes = async () => {
      setLoading(true);
      setError(null);
      try {
        // Construct the full URL
        const apiUrl = `${API_BASE_URL}/v1/ats/resume`;

        const response = await axios.get<ResumeApiResponse>(apiUrl, {
          // Add authentication headers if needed, e.g.:
          // headers: { Authorization: `Bearer ${your_token}` },
          // withCredentials: true, // If using cookies/sessions
        });

        // Axios puts data directly in response.data
        const result = response.data;

        // TODO: Validate the structure of result.data before setting state (optional but recommended)
        if (result.status === 'GET_ALL_RESUMES_SUCCESS' && result.data) {
          // You might need to map the backend data structure to CandidateType if they differ
          setResumes(result.data);
        } else {
          throw new Error(
            result.status || 'Failed to fetch resumes: Invalid response format',
          );
        }
      } catch (e: any) {
        console.error('Failed to fetch resumes:', e);
        // Axios error handling (more specific error details might be available in e.response)
        if (axios.isAxiosError(e)) {
          setError(
            `API Error: ${e.response?.status || e.message}` +
              (e.response?.data?.message ? ` - ${e.response.data.message}` : ''),
          );
        } else {
          setError(e.message || 'An unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResumes();
  }, []); // Empty dependency array ensures this runs only once on mount

  const fuse = useMemo(() => {
    // Use fetched resumes for Fuse instance
    return new Fuse(resumes, {
      keys: [
        'jobTitle', // Adjust keys based on CandidateType and actual data
        'fullname',
        'name', // Added 'name' as it exists in CandidateType
        'skills',
        'position',
        'platform',
        'location',
        'description',
      ],
      threshold: 0.3,
    });
  }, [resumes]); // Update Fuse instance when resumes data changes

  const filteredCandidates: CandidateType[] = useMemo(() => {
    if (!searchTerm) {
      return resumes; // Use fetched resumes
    }
    // Ensure fuse is initialized before searching
    return fuse ? fuse.search(searchTerm).map((result) => result.item) : [];
  }, [searchTerm, resumes, fuse]); // Update filtered list when search or resumes change

  // Back to the first page whenever the search changes the list.
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const pagedCandidates = useMemo(
    () =>
      filteredCandidates.slice(
        page * RESUMES_PAGE_SIZE,
        (page + 1) * RESUMES_PAGE_SIZE,
      ),
    [filteredCandidates, page],
  );

  const showUser = (candidate: CandidateType) => {
    setShowCurrentUser(true);
    setCandidateData(candidate);
  };

  // Display loading state
  if (loading) {
    return <div className="p-6">Loading resumes...</div>; // Basic loading indicator
  }

  // Display error state
  if (error) {
    return (
      <div className="p-6 text-red-600">
        Error loading resumes: {error}
      </div>
    ); // Basic error message
  }

  // Display content once data is loaded
  return (
    <>
      {showCurrentUser ? (
        <div>
          <Button
            icon={RiArrowLeftLine}
            onClick={() => setShowCurrentUser(false)}
            className="mb-4 border-gray-300 bg-white text-gray-900"
            iconPosition="left"
            variant="secondary"
          >
            Back to Page
          </Button>
          {/* Ensure candidateData is not empty before rendering */}
          {Object.keys(candidateData).length > 0 && (
            <ResumeDetailCard
              candidateData={{
                ...(candidateData as CandidateType),
                date: (candidateData as CandidateType).date
                  ? ((candidateData as CandidateType).date instanceof Date
                    ? (candidateData as CandidateType).date.toISOString() // Format Date
                    : String((candidateData as CandidateType).date)) // Ensure string conversion
                  : undefined, // Pass undefined if date is null/undefined
              }}
            />
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-white shadow">
          <div className="flex flex-row items-center justify-between px-6 py-5">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                Resume Management
              </div>
              <div className="text-sm font-normal text-gray-600">
                All candidates whose resumes you have saved by creating a
                profile
              </div>
            </div>

            <div className="flex flex-row gap-4">
              <TextInput
                icon={RiSearchLine}
                placeholder="Search..."
                className="w-auto lg:w-[400px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="h-[1px] !min-w-full bg-gray-300 p-0"></div>
          <div>
            <TabGroup>
              <div className="flex items-center justify-between px-6 pt-2">
                <TabList variant="solid" color="orange">
                  <Tab>Candidates who responded</Tab>
                  <Tab>Saved</Tab>
                </TabList>
                <div className="flex items-center gap-2">
                  <Select className="w-auto" defaultValue="1">
                    <SelectItem value="1">All Position</SelectItem>
                    {/* Dynamically populate options based on fetched data if needed */}
                  </Select>
                  <Select className="w-auto" defaultValue="1">
                    <SelectItem value="1">All Time</SelectItem>
                    {/* Dynamically populate options based on fetched data if needed */}
                  </Select>
                  <Select className="w-auto" defaultValue="1">
                    <SelectItem value="1">All Location</SelectItem>
                    {/* Dynamically populate options based on fetched data if needed */}
                  </Select>
                </div>
              </div>
              <TabPanels>
                <TabPanel>
                  <CandidatesTable
                    showUser={showUser}
                    candidates={pagedCandidates}
                  />
                </TabPanel>
                <TabPanel>
                  {/* Assuming 'Saved' candidates are filtered from the same source or a different endpoint */}
                  <SavedCandidatesTable
                    showUser={showUser}
                    // TODO: Update this logic if saved candidates are fetched separately or filtered differently
                    candidates={filteredCandidates.filter(
                      (c) => c.status === 'Saved',
                    )}
                  />
                </TabPanel>
              </TabPanels>
            </TabGroup>
            <Pagination
              limit={RESUMES_PAGE_SIZE}
              page={page}
              count={filteredCandidates.length}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </>
  );
};
